export const RISK_OVERVIEW_FACTOR_GROUP_ORDER = Object.freeze([
    'Style',
    'Industry',
    'Country',
    'Currency',
    'Market / Regional',
    'Other / Unknown',
]);

const assertRows = (rows, label) => {
    if (!Array.isArray(rows)) {
        throw new Error(`Risk Overview ${label} rows must be an array.`);
    }
};

export function buildRiskFactorSheetRows(summaryRows, groupedRows) {
    assertRows(summaryRows, 'factor summary');
    const rows = [['Portfolio Risk Factor'], []];

    if (summaryRows.length > 0) {
        const headers = Object.keys(summaryRows[0]);
        rows.push(headers);
        summaryRows.forEach((row) => rows.push(headers.map((header) => row[header])));
    }

    RISK_OVERVIEW_FACTOR_GROUP_ORDER.forEach((groupName) => {
        const groupRows = groupedRows?.[groupName];
        if (!Array.isArray(groupRows) || groupRows.length === 0) return;

        const headers = Object.keys(groupRows[0]);
        rows.push([], [groupName], [], headers);
        groupRows.forEach((row) => rows.push(headers.map((header) => row[header])));
    });

    return rows;
}

export function buildRiskOverviewWorkbookModel({
    summaryRows,
    factorSummaryRows,
    factorGroupRows,
    groupRows,
    positionRows,
    groupingSheetName,
}) {
    assertRows(summaryRows, 'summary');
    assertRows(factorSummaryRows, 'factor summary');
    assertRows(groupRows, 'group');
    assertRows(positionRows, 'position');

    return Object.freeze({
        fileName: 'all_risk_data.xlsx',
        sheets: Object.freeze([
            Object.freeze({ name: 'Risk Summary', kind: 'json', rows: summaryRows }),
            Object.freeze({
                name: 'risk_factor',
                kind: 'aoa',
                rows: buildRiskFactorSheetRows(factorSummaryRows, factorGroupRows),
            }),
            Object.freeze({ name: groupingSheetName, kind: 'json', rows: groupRows }),
            Object.freeze({ name: 'position_detail', kind: 'json', rows: positionRows }),
        ]),
    });
}

export async function writeRiskOverviewWorkbook({ fileName, sheets }) {
    const normalizedFileName = String(fileName || '').trim();
    if (!normalizedFileName || !Array.isArray(sheets) || sheets.length === 0) {
        throw new Error('Risk Overview export requires a filename and at least one sheet.');
    }

    const XLSX = await import('xlsx');
    const workbook = XLSX.utils.book_new();

    sheets.forEach(({ name, kind, rows }) => {
        const sheetName = String(name || '').trim();
        assertRows(rows, sheetName || 'sheet');
        if (!sheetName || (kind !== 'json' && kind !== 'aoa')) {
            throw new Error('Risk Overview export contains an invalid sheet descriptor.');
        }
        const worksheet = kind === 'aoa'
            ? XLSX.utils.aoa_to_sheet(rows)
            : XLSX.utils.json_to_sheet(rows);
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    });

    XLSX.writeFile(workbook, normalizedFileName);
}
